import * as React from 'react';
import {style} from 'typestyle';
import {em, percent, rem, viewHeight} from 'csx';
import {mixinBackgroundCover} from '../theme/mixin';
import {aroundJustified, center, centerCenter, horizontal, start, vertical} from 'csstips';
import {constantMaxLandingPageWidth, constantMaxReadingWidth, constantStandardPadding} from '../theme/constant';
import {colorBackgroundAlt, colorBrand, colorWhite} from '../theme/color';
import {mediaTablet} from '../theme/media';
import preventOrphan from '../lib/preventOrphan';
import {ButtonPill} from '../components/button-pill';
import {Component, deps, inject, observer} from '../lib/component';
import {ImageTile} from '../components/image-tile';
import {gradientDark} from '../theme/gradient';
import VectorBackground from '../components/vector-background';
import {ImageAspect} from '../constants';
import InlinePreviewImage from '../components/inline-preview/image';
import articleImage from '../lib/articleImage';
import {LandingImage} from '../modules/page/landing/model';
import {ArticleImage} from '../types/graphql';

interface Props {
  slug: string;
  styles?: typeof LandingV2Page.styles;
}

@inject(deps) @observer
export default class LandingV2Page extends Component<Props> {
  public render() {
    const {styles = LandingV2Page.styles} = this.props;
    const {handleClickSignIn, handleClickLink} = this.props.controller.ui;
    const data = this.props.model.page.landing;

    if (data.loading) return (
      <div className={styles.landing} />
    );

    return (
      <VectorBackground className={styles.landing}>
        <div className={styles.headerContainer}>
          <InlinePreviewImage
            className={styles.headerImage}
            aspect={ImageAspect.BOX}
            image={LandingV2Page.mapImage(data.header_image)}
          />
          <div className={styles.headerContent}>
            <div>
              <h1>{preventOrphan(data.header)}</h1>
              <p>{preventOrphan(data.strapline)}</p>
              <ButtonPill
                bouncing={true}
                primary={true}
                onClick={data.cta_1_signup ? handleClickSignIn : handleClickLink}
                href={!data.cta_1_signup ? data.cta_1_link.url : void 0}
                className={styles.cta}
                children={data.cta_1_copy}
              />
            </div>
          </div>
        </div>
        <div className={styles.heroContainer}>
          <h2>{data.hero_title}</h2>
          <ul>
            <li>
              <InlinePreviewImage
                className={styles.heroImage}
                aspect={ImageAspect.BOX}
                image={LandingV2Page.mapImage(data.hero_1_image)}
              />
              <p>{preventOrphan(data.hero_1_copy)}</p>
            </li>
            <li>
              <InlinePreviewImage
                className={styles.heroImage}
                aspect={ImageAspect.BOX}
                image={LandingV2Page.mapImage(data.hero_2_image)}
              />
              <p>{preventOrphan(data.hero_2_copy)}</p>
            </li>
            <li>
              <InlinePreviewImage
                className={styles.heroImage}
                aspect={ImageAspect.BOX}
                image={LandingV2Page.mapImage(data.hero_3_image)}
              />
              <p>{preventOrphan(data.hero_3_copy)}</p>
            </li>
          </ul>
        </div>
        <InlinePreviewImage
          className={styles.divider}
          image={LandingV2Page.mapImage(data.divider_image)}
          children={(
            <div className={styles.dividerInner}>
              <ButtonPill
                bouncing={true}
                primary={true}
                onClick={data.cta_2_signup ? handleClickSignIn : handleClickLink}
                href={!data.cta_2_signup ? data.cta_2_link.url : void 0}
                className={styles.cta}
                children={data.cta_2_copy}
              />
            </div>
          )}
        />
        <div className={styles.videoContainer}>
          {data.video_title && (
            <h2>{preventOrphan(data.video_title)}</h2>
          )}
          <div className={styles.video}>
            <div dangerouslySetInnerHTML={{__html: data.video.html}} />
          </div>
          <p>{preventOrphan(data.video_copy)}</p>
          <ButtonPill
            bouncing={true}
            primary={true}
            onClick={data.cta_3_signup ? handleClickSignIn : handleClickLink}
            href={!data.cta_3_signup ? data.cta_3_link.url : void 0}
            className={styles.cta}
            children={data.cta_3_copy}
          />
        </div>
      </VectorBackground>
    )
  }

  static mapImage = (image: LandingImage): ArticleImage => articleImage({
    main: image.url,
    caption: image.alt,
    xlarge: image.xlarge.url,
    large: image.large.url,
    medium: image.medium.url,
    small: image.small.url,
    xsmall: image.xsmall.url,
  })

  static styles = {
    landing: style(
      {
        background: gradientDark,
        ...mixinBackgroundCover,
        backgroundPosition: 'top center',
        minHeight: viewHeight(100),
        padding: `0 0 2rem`,
        $nest: {
          '& h1': {
            fontSize: rem(2.25),
            margin: '1.25rem 0'
          },
          '& h2': {
            fontSize: rem(1.75),
            margin: '1.25rem 0'
          },
          '& p': {
            fontWeight: 500,
            fontSize: rem(1.25),
            margin: '1.25rem 0',
          }
        }
      },
      mediaTablet({
        $nest: {
          '& h1': {
            fontSize: rem(3),
          },
          '& h2': {
            fontSize: rem(2),
          },
        }
      })
    ),
    headerContainer: style(
      {
        ...vertical,
        ...center,
        margin: '0 auto',
        maxWidth: constantMaxLandingPageWidth,
        padding: constantStandardPadding,
      },
      mediaTablet({
        ...horizontal,
      })
    ),
    headerContent: style(
      {
        ...centerCenter,
        $nest: {
          '& div': {
            textAlign: 'center',
            maxWidth: constantMaxReadingWidth
          },
          '& h1': {
            textAlign: 'left',
            color: colorWhite.toString(),
          },
          '& p': {
            textAlign: 'left',
            color: colorBackgroundAlt.toString(),
          }
        }
      },
      mediaTablet({
        flex: 1,
        padding: constantStandardPadding,
        marginLeft: rem(1),
      })
    ),
    headerImage: style(
      {
        width: percent(100),
        maxWidth: rem(24),
        margin: rem(1)
      },
      mediaTablet({
        flex: 1,
        maxWidth: 'none'
      })
    ),
    cta: style({
      fontSize: rem(1.125),
      borderRadius: rem(2),
      lineHeight: em(1.4375),
      fontWeight: 600,
      padding: '0.625em 2.5em'
    }),
    heroContainer: style(
      {
        margin: `1.25rem auto`,
        padding: constantStandardPadding,
        width: `calc(${constantMaxLandingPageWidth} + 2rem)`,
        maxWidth: percent(100),
        background: colorBackgroundAlt.toString(),
        color: colorBrand.toString(),
        position: 'relative',
        $nest: {
          '& h2': {
            textAlign: 'center',
          },
          '& ul': {
            ...vertical,
            ...center,
            margin: 0,
            padding: 0,
            listStyle: 'none inside none'
          },
          '& li': {
            ...vertical,
            ...center,
            display: 'flex',
            flexShrink: 0,
            width: rem(16),
            maxWidth: `calc(100% - 4rem)`,
            margin: `0 0 2.5rem 0`,
            $nest: {
              '&:last-child': {
                margin: 0,
              },
              '& p': {
                textAlign: 'center',
                fontSize: rem(1.25),
                maxWidth: rem(12)
              }
            }
          },
        }
      },
      mediaTablet({
        maxWidth: `calc(100% - 2rem)`,
        $nest: {
          '& ul': {
            ...horizontal,
            ...start,
            ...aroundJustified,
          },
          '& li': {
            maxWidth: `calc((100% / 3) - 5rem)`,
            margin: 0,
          }
        }
      })
    ),
    heroImage: style({
      width: percent(100),
      margin: `1.25rem 0`
    }),
    divider: style({
      ...mixinBackgroundCover,
      ...centerCenter,
      width: percent(100),
      height: rem(15),
      margin: `2.5rem 0`,
    }),
    dividerInner: style({
      ...centerCenter,
    }),
    videoContainer: style({
      ...vertical,
      ...center,
      width: percent(100),
      margin: '0 auto',
      $nest: {
        '& p': {
          textAlign: 'center',
          margin: `2.5rem 0`,
          color: colorBackgroundAlt.toString(),
          width: constantMaxReadingWidth,
          maxWidth: `calc(100% - 2rem)`
        },
        '& h2': {
          textAlign: 'center',
          margin: '0 0 2.5rem',
          color: colorWhite.toString(),
          width: constantMaxReadingWidth,
          maxWidth: `calc(100% - 2rem)`
        }
      }
    }),
    video: style(
      {
        width: percent(100),
        maxWidth: `calc(100% - 2rem)`,
        position: 'relative',
        $nest: {
          '& > div': {
            paddingTop: `calc(1000%/16)`,
            position: 'relative',
          },
          '& iframe': {
            width: percent(100),
            height: percent(100),
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }
        }
      },
      mediaTablet({
        maxWidth: `calc(${constantMaxReadingWidth} + 4rem)`
      })
    ),
  };
}
